// CurrentYearStackedBarChart.jsx

import React from 'react';
import { ResponsiveBar } from '@nivo/bar';

/**
 * Applies the same "val * 1000 => format => M/K" logic 
 * you use in TrendChartNivo. 
 */
function formatEmployment(valInThousands) {
  // Convert from thousands to actual
  const actualVal = valInThousands * 1000;
  
  if (Math.abs(actualVal) >= 1_000_000) {
    return (actualVal / 1_000_000).toFixed(1) + 'M';
  }
  if (Math.abs(actualVal) >= 1_000) {
    return (actualVal / 1_000).toFixed(1) + 'K';
  }
  return Math.round(actualVal).toString();
}

/**
 * Stacked bar that shows:
 *   - Blue portion: industryVal
 *   - Gray portion: remainderVal = totalVal - industryVal
 * representing how big the industry is vs. total nonfarm in 2021.
 *
 * @param {string} industryName - For tooltip display.
 * @param {number} industryVal - Industry's raw (in thousands) 2021 employment.
 * @param {number} totalVal - Total nonfarm (in thousands) 2021 employment.
 */
export default function CurrentYearStackedBarChart({ industryName, industryVal, totalVal }) {
  // Guard: if any are missing or totalVal <= 0, don't render
  if (industryVal == null || totalVal == null || totalVal <= 0) {
    return null;
  }

  const remainderVal = Math.max(totalVal - industryVal, 0);

  // We'll store one row of data for Nivo to display a stacked bar
  const data = [
    {
      id: 'Stacked',
      industryVal: industryVal,
      remainderVal: remainderVal,
    },
  ];

  // For tooltip usage, let's precompute share
  const sharePct = (industryVal / totalVal) * 100;

  return (
    <div style={{ width: '120px', height: '20px' }}>
      <ResponsiveBar
        data={data}
        keys={['industryVal', 'remainderVal']}
        indexBy="id"
        layout="horizontal"
        groupMode="stacked"
        margin={{ top: 0, right: 0, bottom: 0, left: 0 }}
        padding={0}
        minValue={0}
        maxValue={totalVal}
        colors={['#007acc', '#ccc']} 
        enableGridX={false}
        enableGridY={false}
        axisLeft={null}
        axisRight={null}
        axisTop={null}
        axisBottom={null}
        enableLabel={false}
        borderRadius={2}
        tooltip={(bar) => {
          // Use the big-number formatting for the industry portion:
          const formattedIndustryVal = formatEmployment(industryVal);

          return (
            <div
              style={{
                background: 'white',
                border: '1px solid #ccc',
                padding: '6px 8px',
                width: '200px',
              }}
            >
              <strong>{industryName}</strong> makes up{' '}
              <strong>{sharePct.toFixed(1)}%</strong> ({formattedIndustryVal}) of
              total nonfarm in 2021.
            </div>
          );
        }}
      />
    </div>
  );
}